import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Egg, ArrowLeft, Users, Calendar, DollarSign, CheckCircle2,
  ShieldCheck, Clock, Baby, Loader2, LogIn, ChevronRight, Lock,
} from 'lucide-react';

const STATUS_MAP = {
  planned: { label: 'Planeada', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20', icon: Clock },
  paired: { label: 'Emparejada', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: Users },
  ovulating: { label: 'Ovulando', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20', icon: Egg },
  laid: { label: 'Puesta', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Egg },
  incubating: { label: 'Incubando', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', icon: Clock },
  hatching: { label: 'Por nacer', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: Baby },
  available: { label: 'Disponible', color: 'bg-emerald-600/10 text-emerald-300 border-emerald-500/20', icon: CheckCircle2 },
  assigned: { label: 'Asignada', color: 'bg-teal-500/10 text-teal-400 border-teal-500/20', icon: ShieldCheck },
  completed: { label: 'Completada', color: 'bg-zinc-600/10 text-zinc-500 border-zinc-500/20', icon: CheckCircle2 },
};

const TIMELINE_ORDER = ['planned','paired','ovulating','laid','incubating','hatching','available','assigned','completed'];

export default function PublicClutchPage() {
  const { clutchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clutch, setClutch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myWaitlistEntry, setMyWaitlistEntry] = useState(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    loadClutch();
  }, [clutchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadClutch = async () => {
    try {
      const data = await api.getClutch(clutchId);
      setClutch(data);
      if (user) {
        try {
          const wl = await api.getClutchWaitlist(clutchId);
          const mine = (wl.waitlist || []).find(w => w.user_id === user.id);
          setMyWaitlistEntry(mine || null);
        } catch (e) { console.error('Waitlist check error:', e); }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!user) { navigate('/login'); return; }
    setJoining(true);
    try {
      await api.joinClutchWaitlist(clutchId, { notes: '', deposit_intent: false });
      toast.success('Te uniste a la waitlist');
      loadClutch();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setJoining(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0A0A0C] pt-24 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
    </div>
  );

  if (!clutch) return (
    <div className="min-h-screen bg-[#0A0A0C] pt-24 text-center py-20">
      <p className="text-zinc-500">Camada no encontrada</p>
      <Button variant="link" onClick={() => navigate('/explorar')} className="text-emerald-400 mt-4">Volver a explorar</Button>
    </div>
  );

  const st = STATUS_MAP[clutch.status] || STATUS_MAP.planned;
  const StatusIcon = st.icon;
  const currentIndex = TIMELINE_ORDER.indexOf(clutch.status);
  const requiresDeposit = clutch.waitlist_mode === 'deposit' || clutch.waitlist_mode === 'both';
  const isFreeWaitlist = clutch.waitlist_mode === 'free' || clutch.waitlist_mode === 'both';

  return (
    <div className="min-h-screen bg-[#0A0A0C] pt-24 pb-16" data-testid="public-clutch-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Back */}
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-zinc-500 hover:text-zinc-300 mb-6 transition-colors" data-testid="clutch-back-btn">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>

        {/* Hero */}
        <div className="glass-card p-8 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Images */}
            {clutch.parent_images?.length > 0 ? (
              <div className="flex gap-3 flex-shrink-0">
                {clutch.parent_images.slice(0, 3).map((img, i) => (
                  <img key={img} src={img} alt="Padre/Madre" className="w-28 h-28 rounded-xl object-cover border border-white/5" />
                ))}
              </div>
            ) : (
              <div className="w-28 h-28 rounded-xl bg-zinc-800 flex items-center justify-center flex-shrink-0">
                <Egg className="w-10 h-10 text-zinc-600" />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border ${st.color}`}>
                  <StatusIcon className="w-3.5 h-3.5" />{st.label}
                </span>
                {requiresDeposit && (
                  <span className="text-xs px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                    <DollarSign className="w-3 h-3 inline" /> Requiere deposito
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-light tracking-tight text-zinc-100 font-[Outfit] mb-1">
                {clutch.species_name} {clutch.morph_expected && `- ${clutch.morph_expected}`}
              </h1>
              <p className="text-zinc-500 text-sm flex items-center gap-2 flex-wrap">
                <span>por <span className="text-zinc-300">{clutch.breeder_name}</span></span>
                {clutch.estimated_hatch_date && (
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Hatch estimado: {clutch.estimated_hatch_date}</span>
                )}
              </p>

              {/* Prices */}
              {(clutch.price_range_min > 0 || clutch.price_range_max > 0) && (
                <p className="text-lg text-zinc-200 mt-3 font-medium">
                  ${clutch.price_range_min?.toLocaleString('es-MX')} - ${clutch.price_range_max?.toLocaleString('es-MX')} MXN
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left column */}
          <div className="md:col-span-2 space-y-6">
            {/* Timeline */}
            <div className="glass-card p-6">
              <h2 className="text-lg font-medium text-zinc-100 mb-4">Estado de la camada</h2>
              <div className="flex items-center gap-1 overflow-x-auto pb-2">
                {TIMELINE_ORDER.slice(0, 7).map((s, i) => {
                  const sInfo = STATUS_MAP[s];
                  const isActive = i <= currentIndex;
                  const isCurrent = s === clutch.status;
                  return (
                    <div key={s} className="flex items-center gap-1 flex-shrink-0">
                      <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-all ${
                        isCurrent ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' :
                        isActive ? 'bg-white/5 text-zinc-300' : 'text-zinc-600'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${isCurrent ? 'bg-emerald-400' : isActive ? 'bg-zinc-400' : 'bg-zinc-700'}`} />
                        {sInfo.label}
                      </div>
                      {i < 6 && <ChevronRight className={`w-3 h-3 flex-shrink-0 ${isActive ? 'text-zinc-400' : 'text-zinc-700'}`} />}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Details */}
            <div className="glass-card p-6">
              <h2 className="text-lg font-medium text-zinc-100 mb-4">Detalles</h2>
              <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                {clutch.sire_info && (
                  <div>
                    <span className="text-zinc-500 block text-xs mb-0.5">Sire (Padre)</span>
                    <span className="text-zinc-200">{clutch.sire_info}</span>
                  </div>
                )}
                {clutch.dam_info && (
                  <div>
                    <span className="text-zinc-500 block text-xs mb-0.5">Dam (Madre)</span>
                    <span className="text-zinc-200">{clutch.dam_info}</span>
                  </div>
                )}
                <div>
                  <span className="text-zinc-500 block text-xs mb-0.5">Cantidad esperada</span>
                  <span className="text-zinc-200">{clutch.expected_count || 'Por confirmar'}</span>
                </div>
                {clutch.genetic_probability && (
                  <div className="col-span-2">
                    <span className="text-zinc-500 block text-xs mb-0.5">Probabilidad genetica</span>
                    <span className="text-zinc-200">{clutch.genetic_probability}</span>
                  </div>
                )}
                {clutch.breeder_notes && (
                  <div className="col-span-2">
                    <span className="text-zinc-500 block text-xs mb-0.5">Notas del breeder</span>
                    <span className="text-zinc-200">{clutch.breeder_notes}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Waitlist rules */}
            <div className="glass-card p-6">
              <h2 className="text-lg font-medium text-zinc-100 mb-4">Reglas de la waitlist</h2>
              <div className="space-y-3 text-sm">
                {isFreeWaitlist && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03]">
                    <Users className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-zinc-200 font-medium">Waitlist libre (FIFO)</p>
                      <p className="text-zinc-500 text-xs mt-0.5">Unete sin costo. El orden de llegada determina tu prioridad. El breeder puede ajustar posiciones.</p>
                    </div>
                  </div>
                )}
                {requiresDeposit && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03]">
                    <DollarSign className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-zinc-200 font-medium">Deposito de reserva: ${clutch.deposit_amount?.toLocaleString('es-MX')} MXN</p>
                      <p className="text-zinc-500 text-xs mt-0.5">Asegura tu lugar con un deposito. Quienes depositen tienen prioridad sobre la lista libre.</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03]">
                  <ShieldCheck className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-zinc-200 font-medium">Asignacion por el breeder</p>
                    <p className="text-zinc-500 text-xs mt-0.5">Al nacer las crias, el breeder asigna individuos segun la posicion en waitlist.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right sidebar */}
          <div className="space-y-6">
            {/* CTA Card */}
            <div className="glass-card p-6 border border-emerald-500/10">
              <h3 className="text-zinc-100 font-medium mb-2">Asegura tu lugar antes del hatch</h3>
              <p className="text-zinc-500 text-xs mb-4">
                {clutch.waitlist_count || 0} personas en la lista de espera
              </p>

              {!user ? (
                <div className="space-y-3">
                  <p className="text-xs text-zinc-500">Inicia sesion para unirte a la waitlist</p>
                  <Link to="/login" className="w-full">
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="clutch-login-cta">
                      <LogIn className="w-4 h-4 mr-2" /> Iniciar sesion
                    </Button>
                  </Link>
                  <Link to="/registro" className="block text-center text-xs text-emerald-400 hover:underline">
                    Crear cuenta gratis
                  </Link>
                </div>
              ) : myWaitlistEntry ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="text-emerald-400 text-sm font-medium">En la waitlist</p>
                      <p className="text-emerald-400/60 text-xs">Posicion #{myWaitlistEntry.position}</p>
                    </div>
                  </div>
                  {myWaitlistEntry.deposit_status === 'confirmed' && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 text-amber-400 text-xs">
                      <DollarSign className="w-4 h-4" /> Deposito confirmado
                    </div>
                  )}
                  {requiresDeposit && myWaitlistEntry.deposit_status !== 'confirmed' && (
                    <Button
                      variant="outline"
                      className="w-full border-amber-500/20 text-amber-400 hover:bg-amber-500/10"
                      onClick={async () => {
                        try {
                          await api.createClutchDeposit(clutchId, { waitlist_id: myWaitlistEntry.id });
                          toast.success('Deposito registrado');
                          loadClutch();
                        } catch (e) { toast.error(formatApiError(e)); }
                      }}
                      data-testid="clutch-deposit-btn"
                    >
                      <DollarSign className="w-4 h-4 mr-2" /> Depositar ${clutch.deposit_amount?.toLocaleString('es-MX')} MXN
                    </Button>
                  )}
                </div>
              ) : clutch.status !== 'completed' ? (
                <div className="space-y-3">
                  <Button onClick={handleJoin} disabled={joining} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="clutch-join-btn">
                    {joining ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                    Unirme a la waitlist
                  </Button>
                  <p className="text-zinc-600 text-[10px] text-center">Proximas crias disponibles para reserva</p>
                </div>
              ) : (
                <p className="text-zinc-500 text-sm text-center">Esta camada ya fue completada</p>
              )}
            </div>

            {/* Breeder info */}
            <div className="glass-card p-6">
              <h3 className="text-zinc-400 text-xs uppercase tracking-wider mb-3">Breeder</h3>
              <div className="flex items-center gap-3">
                {clutch.breeder_avatar ? (
                  <img src={clutch.breeder_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-400 font-medium text-sm">
                    {(clutch.breeder_name || '?')[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="text-zinc-200 text-sm font-medium">{clutch.breeder_name}</p>
                  <p className="text-zinc-500 text-xs">Criador verificado</p>
                </div>
              </div>
            </div>

            {/* Quick stats */}
            <div className="glass-card p-6">
              <h3 className="text-zinc-400 text-xs uppercase tracking-wider mb-3">Resumen</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Esperadas</span>
                  <span className="text-zinc-200">{clutch.expected_count || '?'} crias</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">En waitlist</span>
                  <span className="text-zinc-200">{clutch.waitlist_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Modo</span>
                  <span className="text-zinc-200">
                    {clutch.waitlist_mode === 'free' ? 'Libre' : clutch.waitlist_mode === 'deposit' ? 'Solo deposito' : 'Libre + deposito'}
                  </span>
                </div>
                {clutch.actual_count > 0 && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Nacidas</span>
                    <span className="text-emerald-400">{clutch.actual_count}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
